import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { onAuthStateChanged, onIdTokenChanged, type User } from 'firebase/auth'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { doc } from 'firebase/firestore'
import { auth, db, ensureAnonAuth } from '../lib/firebase'
import { uploadAvatar } from '../lib/avatarUpload'
import { getDoc, setDoc, updateDoc } from '../lib/firestore'
import { Sentry } from '../lib/sentry'
import { authStateLabel, setAnalyticsAuthState, setAnalyticsUser, setAnalyticsUserProperty } from '../lib/analytics'
import { markAuthResolved, isCold } from '../lib/perf'
import { log } from '../lib/logger'

const AUTH_TIMEOUT_MS = isCold ? 0 : 1000
import { AuthContext } from './AuthContext'

// Persists across remounts (e.g. locale change causes full tree remount via fbtee)
// so we don't re-sync the profile on every re-subscription.
let lastSyncedUid: string | null = null

// Re-host Google's photo in our own Storage so the URL doesn't expire when
// Google rotates its image IDs. Tries a normal fetch first (cold first-sign-in
// path), falls back to force-cache (avoids Google's per-URL rate limit on
// repeat fetches when an <img> render has already warmed the HTTP cache).
async function rehostGooglePhoto(uid: string, googlePhotoURL: string): Promise<string | null> {
  const fetchPhoto = async (cache?: RequestCache): Promise<Blob | null> => {
    try {
      const res = await fetch(googlePhotoURL, {
        ...(cache ? { cache } : {}),
        referrerPolicy: 'no-referrer',
      })
      return res.ok ? await res.blob() : null
    } catch {
      return null
    }
  }

  try {
    const blob = (await fetchPhoto()) ?? (await fetchPhoto('force-cache'))
    if (!blob) {
      Sentry.captureMessage('Failed to fetch Google profile photo for rehosting', 'warning')
      return null
    }
    return await uploadAvatar(uid, blob)
  } catch (err) {
    Sentry.captureException(err)
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  // Bumped when the same User object changes in place (an anonymous session
  // linked to a provider), which setUser alone can't signal.
  const [userRevision, setUserRevision] = useState(0)

  // Lets every GA4 report leave the team's own usage out.
  useEffect(() => setAnalyticsUserProperty('is_admin', String(isAdmin)), [isAdmin])

  useEffect(() => {
    log('auth', 'listener registered')
    // The uid the admin claim was last successfully resolved for. A failed read
    // must not clobber an already-resolved claim — on flaky reception that would
    // silently hide admin UI until the next full reload. Only a different user
    // (or sign-out) clears it.
    let adminClaimUid: string | null = null
    const syncAdminClaim = async (firebaseUser: User, forceRefresh = false) => {
      try {
        const { claims } = await firebaseUser.getIdTokenResult(forceRefresh)
        adminClaimUid = firebaseUser.uid
        setIsAdmin(claims['admin'] === true)
        return true
      } catch (err) {
        if (adminClaimUid !== firebaseUser.uid) setIsAdmin(false)
        log('auth', 'getIdTokenResult failed (offline?)', { error: String(err) })
        return false
      }
    }

    const timer = setTimeout(() => {
      log('auth', 'timeout — unblocking render', { timeout_ms: AUTH_TIMEOUT_MS, is_cold: isCold })
      setLoading(false)
    }, AUTH_TIMEOUT_MS)

    const syncUser = async (firebaseUser: User) => {
      Sentry.setUser({ id: firebaseUser.uid })
      setAnalyticsUser(firebaseUser.isAnonymous ? null : firebaseUser.uid)
      await syncAdminClaim(firebaseUser)
      if (!firebaseUser.isAnonymous && lastSyncedUid !== firebaseUser.uid) {
        lastSyncedUid = firebaseUser.uid
        try {
          const userRef = doc(db, 'users', firebaseUser.uid)
          const existingDoc = await getDoc(userRef)
          const authDisplayName =
            firebaseUser.displayName?.trim() ||
            firebaseUser.email?.split('@')[0] ||
            'User'

          const existingData = existingDoc.exists() ? existingDoc.data() : null
          const googlePhotoURL = firebaseUser.photoURL
          const syncedPhotoURL =
            !existingData?.photoURL && googlePhotoURL
              ? await rehostGooglePhoto(firebaseUser.uid, googlePhotoURL)
              : null

          // The session may have changed while we awaited; the write would be denied.
          // Clear the marker so this uid syncs again when it signs back in.
          if (auth.currentUser?.uid !== firebaseUser.uid) {
            lastSyncedUid = null
            return
          }
          if (!existingData) {
            setDoc(userRef, {
              displayName: authDisplayName,
              ...(syncedPhotoURL ? { photoURL: syncedPhotoURL } : {}),
            })
          } else {
            if (!existingData.displayName?.trim()) {
              updateDoc(userRef, { displayName: authDisplayName })
            }
            if (syncedPhotoURL) {
              updateDoc(userRef, { photoURL: syncedPhotoURL })
            }
          }
        } catch (err) {
          log('auth', 'profile sync failed (offline?)', { error: String(err) })
        }
      }
    }

    // Linking keeps the uid, so onAuthStateChanged never fires for it; the
    // token listener does, and is where an anonymous session becomes a member.
    let anonUid: string | null = null
    const unsubscribeToken = onIdTokenChanged(auth, firebaseUser => {
      if (!firebaseUser || firebaseUser.uid !== anonUid || firebaseUser.isAnonymous) return
      anonUid = null
      const state = authStateLabel(firebaseUser)
      log('auth', `state — ${state} (linked)`)
      setAnalyticsAuthState(state)
      setUserRevision(r => r + 1)
      void syncUser(firebaseUser)
    })

    const unsubscribe = onAuthStateChanged(auth, async firebaseUser => {
      clearTimeout(timer)
      const state = authStateLabel(firebaseUser)
      log('auth', `state — ${state}`)
      anonUid = firebaseUser?.isAnonymous ? firebaseUser.uid : null
      setUser(firebaseUser)
      setLoading(false)
      markAuthResolved(state)
      // Set before anything below can fire an event, so auth_state is never stale.
      setAnalyticsAuthState(state)
      // Pre-trigger anonymous sign-in for invite-link cold loads so JoinPage
      // and FollowTotemPage don't have to wait for their own post-mount effect
      // to fire signin. Path-scoped so casual /home visitors don't get an anon
      // UID minted.
      const path = window.location.pathname
      if (!firebaseUser && (path.startsWith('/join/') || path.startsWith('/t/'))) {
        log('auth','ensureAnonAuth start')
        // The landing page's useGuestLandingSession shares this promise and
        // toasts the failure; offline is expected there, anything else isn't.
        ensureAnonAuth()
          .then(() => log('auth','ensureAnonAuth done'))
          .catch((err: unknown) => {
            log('auth', 'ensureAnonAuth failed', { error: String(err) })
            if ((err as { code?: string })?.code !== 'auth/network-request-failed') Sentry.captureException(err)
          })
      }
      if (firebaseUser) {
        await syncUser(firebaseUser)
      } else {
        lastSyncedUid = null
        adminClaimUid = null
        setIsAdmin(false)
        Sentry.setUser(null)
        setAnalyticsUser(null)
      }
    })
    // Force-refresh the auth token on native app resume — prevents stale-token
    // PERMISSION_DENIED errors after long background periods on iOS. Refreshing
    // via getIdTokenResult also re-derives the admin claim, so a claim that
    // failed to resolve at boot recovers on the next resume instead of waiting
    // for a full reload.
    let resumeSub: { remove: () => void } | undefined
    if (Capacitor.isNativePlatform()) {
      void App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive && auth.currentUser) {
          const ok = await syncAdminClaim(auth.currentUser, true)
          log('auth', ok ? 'token refreshed on resume' : 'token refresh on resume failed')
        }
      }).then(h => { resumeSub = h })
    }

    // Web/PWA equivalent of the resume path: retry a claim that never resolved
    // once connectivity comes back.
    const handleOnline = () => {
      const current = auth.currentUser
      if (current && adminClaimUid !== current.uid) void syncAdminClaim(current, true)
    }
    window.addEventListener('online', handleOnline)

    return () => {
      clearTimeout(timer)
      unsubscribe()
      unsubscribeToken()
      resumeSub?.remove()
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  const signedInUid = useMemo(
    () => (user && !user.isAnonymous ? user.uid : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- userRevision tracks in-place changes to user
    [user, userRevision],
  )

  return <AuthContext.Provider value={{ user, loading, isAdmin, signedInUid }}>{children}</AuthContext.Provider>
}
